import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  bookAllDisruption,
  getStoredDisruptionPlan,
  planDisruptionAllocation,
} from '@/lib/bulk-allocation/api';
import type {
  AllocationPlan,
  AllocationPlanEnvelope,
  BookAllResponse,
} from '@/lib/bulk-allocation/types';

// Event-scope bulk allocation hooks — the disruption "Hotels" tab's
// review-then-book-all. The built plan is the operators' SHARED working state for
// the disruption: it's stored server-side (BFF Redis, TTL'd) and pushed live to
// the disruption WS room, NOT held in one operator's browser. So:
//   - useDisruptionAllocationPlan → GET the shared stored plan on mount (any
//     operator sees what someone else built); the WS DisruptionAllocationPlanChanged
//     event lands live updates into this same cache key (apply-event.ts).
//   - useBuildDisruptionPlan → POST to (re)build; the BFF persists + broadcasts.
//   - useBookAllDisruption → POST to book; the BFF stamps results onto the shared
//     plan + broadcasts.

/** Past this age the chip nudges a re-plan (offers are time-bounded upstream). */
export const PLAN_STALE_MS = 15 * 60_000;

const planKey = (disruptionUrn: string) =>
  ['disruptions', disruptionUrn, 'allocation-plan'] as const;

/** The disruption's SHARED stored plan (whoever built it), loaded on mount.
 *  `null` = none built yet / it expired (Redis TTL). Live updates arrive over the
 *  WS — never a poll. */
export function useDisruptionAllocationPlan(disruptionUrn: string) {
  return useQuery<AllocationPlanEnvelope | null>({
    queryKey: planKey(disruptionUrn),
    queryFn: () => getStoredDisruptionPlan(disruptionUrn),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

/** Explicit (re)build. The BFF persists the plan + broadcasts it; we invalidate so
 *  the shared GET re-reads the freshly stored envelope (the WS event also lands it
 *  live for this operator + everyone else). */
export function useBuildDisruptionPlan(disruptionUrn: string) {
  const qc = useQueryClient();
  return useMutation<AllocationPlan, Error, void>({
    mutationFn: () => planDisruptionAllocation(disruptionUrn),
    onSuccess: () => void qc.invalidateQueries({ queryKey: planKey(disruptionUrn) }),
  });
}

export function useBookAllDisruption(disruptionUrn: string) {
  const qc = useQueryClient();
  return useMutation<BookAllResponse, Error, AllocationPlan>({
    mutationFn: (plan) => bookAllDisruption(disruptionUrn, plan),
    onSuccess: () => {
      // The BFF stamps results onto the stored envelope + broadcasts; re-read it,
      // and nudge the disruption progress + roster rollups (reservations settle
      // live over each case WS room).
      void qc.invalidateQueries({ queryKey: planKey(disruptionUrn) });
      void qc.invalidateQueries({ queryKey: ['disruptions', disruptionUrn, 'progress'] });
      void qc.invalidateQueries({ queryKey: ['disruptions', disruptionUrn, 'roster'] });
    },
  });
}
